/**
 * PaymentMethodCard — обновлённая версия для ЮKassa
 * src/features/subscription/PaymentMethodCard.tsx
 *
 * Изменения vs оригинал:
 * - Убраны все упоминания Stripe Portal
 * - Кнопка "Изменить" теперь инициирует новый платёж ЮKassa
 * - Добавлены карточные бренды МИР (уже был) и унбрандованный fallback
 */

import { useEffect, useState } from 'react';
import { C, F } from '../../design-system/tokens';
import { fetchCompanyPaymentMethods, type CompanyPaymentMethod } from '../superadmin/api-subscriptions';

const BRAND_LABELS: Record<string, string> = {
  visa:       'Visa',
  mastercard: 'Mastercard',
  amex:       'American Express',
  discover:   'Discover',
  diners:     'Diners Club',
  jcb:        'JCB',
  unionpay:   'UnionPay',
  mir:        'МИР',
  unknown:    'Карта',
};

const BRAND_COLORS: Record<string, string> = {
  visa:       '#1A1F71',
  mastercard: '#EB001B',
  amex:       '#2E77BC',
  mir:        '#1E9E5E',
};

function brandLabel(brand: string | null): string {
  return brand ? (BRAND_LABELS[brand.toLowerCase()] ?? brand) : 'Карта';
}

function isExpiringSoon(expMonth: number | null, expYear: number | null): boolean {
  if (!expMonth || !expYear) return false;
  const now = new Date();
  const expiryDate = new Date(expYear, expMonth - 1, 1);
  const diff = expiryDate.getTime() - now.getTime();
  return diff >= 0 && diff < 60 * 24 * 60 * 60 * 1000;
}

function isExpired(expMonth: number | null, expYear: number | null): boolean {
  if (!expMonth || !expYear) return false;
  const expiryEnd = new Date(expYear, expMonth, 0, 23, 59, 59);
  return expiryEnd < new Date();
}

interface PaymentMethodCardProps {
  companyId: string;
  /** Вызывается при нажатии "Изменить карту" */
  onUpdateCard: () => void;
  updateCardBusy?: boolean;
}

export function PaymentMethodCard({
  companyId,
  onUpdateCard,
  updateCardBusy = false,
}: PaymentMethodCardProps) {
  const [methods, setMethods]   = useState<CompanyPaymentMethod[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    setLoading(true);
    fetchCompanyPaymentMethods(companyId)
      .then((rows) => { if (alive) setMethods(rows); })
      .catch(() => { if (alive) setMethods([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [companyId]);

  if (loading) {
    return (
      <div style={{
        padding: '12px 14px',
        borderRadius: 12,
        border: `1px solid ${C.b}`,
        background: C.surf,
        fontSize: 12,
        color: C.sub,
      }}>
        Загружаем способ оплаты…
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div style={{
        padding: '12px 14px',
        borderRadius: 12,
        border: `1px solid ${C.b}`,
        background: C.surf,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
      }}>
        <div>
          <div style={{ fontSize: 12, fontFamily: F.head, fontWeight: 700, color: C.sub }}>
            Способ оплаты не привязан
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>
            Добавьте карту при следующем платеже
          </div>
        </div>
        <button
          onClick={onUpdateCard}
          disabled={updateCardBusy}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            border: `1px solid ${C.acc}`,
            background: 'transparent',
            color: C.acc,
            fontSize: 12,
            fontFamily: F.head,
            fontWeight: 700,
            cursor: updateCardBusy ? 'not-allowed' : 'pointer',
            opacity: updateCardBusy ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          {updateCardBusy ? '…' : 'Добавить карту'}
        </button>
      </div>
    );
  }

  const primary = methods.find((m) => m.is_default) ?? methods[0];
  const expiring = isExpiringSoon(primary.exp_month, primary.exp_year);
  const expired  = isExpired(primary.exp_month, primary.exp_year);
  const accentColor = expired ? C.re : expiring ? C.am : C.gr;
  const expiryLabel =
    primary.exp_month && primary.exp_year
      ? `${String(primary.exp_month).padStart(2, '0')}/${primary.exp_year}`
      : null;

  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: 12,
      border: `1.5px solid ${expired || expiring ? `${accentColor}55` : C.b}`,
      background: C.surf,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      {/* Card icon */}
      <div style={{
        width: 42,
        height: 28,
        borderRadius: 5,
        background: primary.brand ? (BRAND_COLORS[primary.brand.toLowerCase()] ?? C.bg) : C.bg,
        border: `1px solid ${C.b}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 9,
        fontWeight: 800,
        color: '#fff',
        letterSpacing: '.5px',
      }}>
        {brandLabel(primary.brand).slice(0, 4).toUpperCase()}
      </div>

      {/* Card details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontFamily: F.head, fontWeight: 700, color: C.txt }}>
          {brandLabel(primary.brand)} •••• {primary.last4 ?? '????'}
        </div>
        {expiryLabel && (
          <div style={{ fontSize: 11, color: expired || expiring ? accentColor : C.sub, marginTop: 2 }}>
            {expired ? '⚠ Истекла' : expiring ? '⚠ Истекает' : 'Действительна до'} {expiryLabel}
          </div>
        )}
        <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
          ЮKassa • {primary.provider ?? 'yookassa'}
        </div>
      </div>

      {/* Update button */}
      <button
        onClick={onUpdateCard}
        disabled={updateCardBusy}
        style={{
          padding: '6px 12px',
          borderRadius: 8,
          border: `1px solid ${C.b}`,
          background: 'transparent',
          color: C.sub,
          fontSize: 12,
          fontFamily: F.head,
          fontWeight: 700,
          cursor: updateCardBusy ? 'not-allowed' : 'pointer',
          opacity: updateCardBusy ? 0.6 : 1,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {updateCardBusy ? '…' : 'Изменить'}
      </button>
    </div>
  );
}
